"use client";

import { ReactLenis, useLenis } from "lenis/react";
import type { LenisOptions } from "lenis";
import { useEffect, useState, type ReactNode } from "react";

import "lenis/dist/lenis.css";

const LENIS_OPTIONS: LenisOptions = {
  autoRaf: true,
  duration: 1.2,
  lerp: 0.1,
  // Wheel input follows the browser immediately. Keep Lenis for explicit anchor
  // animations, scroll listeners and the existing menu/dialog scroll locks.
  smoothWheel: false,
  anchors: true,
};

type CcSmoothScrollProps = {
  children: ReactNode;
};

function MotionPreference() {
  const lenis = useLenis();
  useEffect(() => {
    if (!lenis) return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => {
      // Update the existing instance; changing ReactLenis options recreates it.
      lenis.options.smoothWheel = false;
      lenis.options.anchors = !media.matches;
      lenis.options.duration = media.matches ? 0 : LENIS_OPTIONS.duration;
    };
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, [lenis]);
  return null;
}

export function CcSmoothScroll({ children }: CcSmoothScrollProps) {
  const [options] = useState<LenisOptions>(() => {
    const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    return reduced ? { ...LENIS_OPTIONS, smoothWheel: false, anchors: false, duration: 0 } : LENIS_OPTIONS;
  });
  return (
    <ReactLenis root options={options}>
      <MotionPreference />
      {children}
    </ReactLenis>
  );
}
